import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { Badge, Button, cn, type Tone } from "@/components/ui";
import { confidenceBand } from "@/lib/finance";
import type { Trace, VerificationStatus } from "@/lib/types";
import { useCase } from "./case-context";

/**
 * Traceability.
 *
 * The product principle is that any financial conclusion can be walked back to
 * the page it came from. That only works if it is one mechanism used
 * everywhere, so there is exactly one of these: overview risks, analysis rows
 * and decision drivers all open the same drawer.
 *
 * The drawer is a drawer rather than a navigation because the analyst is
 * checking a claim, not leaving the screen. Following through to the Evidence
 * tab stays available inside it.
 */
interface TraceContextValue {
  open: (trace: Trace) => void;
}

const TraceContext = createContext<TraceContextValue | null>(null);

export function TraceProvider({ children }: { children: ReactNode }) {
  const [trace, setTrace] = useState<Trace | null>(null);
  const value = useMemo<TraceContextValue>(() => ({ open: setTrace }), []);

  return (
    <TraceContext.Provider value={value}>
      {children}
      {trace ? <TraceDrawer trace={trace} onClose={() => setTrace(null)} /> : null}
    </TraceContext.Provider>
  );
}

/**
 * A claim the analyst can interrogate. Renders as plain text when there is no
 * trace behind it — an unsourced claim must not look sourced.
 */
export function TraceLink({
  trace,
  children,
  className,
}: {
  trace?: Trace;
  children: ReactNode;
  className?: string;
}) {
  const context = useContext(TraceContext);

  if (!trace || !context) return <span className={className}>{children}</span>;

  return (
    <button
      type="button"
      onClick={() => context.open(trace)}
      title={`Trace to ${trace.evidence.documentName}`}
      className={cn(
        "cursor-pointer text-left underline decoration-line-strong decoration-dotted underline-offset-4",
        "transition-colors hover:text-accent hover:decoration-accent",
        className,
      )}
    >
      {children}
    </button>
  );
}

const STATUS_TONE: Record<VerificationStatus, Tone> = {
  verified: "positive",
  unverified: "caution",
  flagged: "negative",
};

function TraceDrawer({ trace, onClose }: { trace: Trace; onClose: () => void }) {
  const { case: kase, evidence } = useCase();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const source = evidence.find((entry) => entry.id === trace.evidence.documentId);
  const value = trace.evidence.extractedValueId
    ? source?.extracted.find((entry) => entry.id === trace.evidence.extractedValueId)
    : undefined;

  const evidenceHref = `/cases/${kase.id}/evidence#${trace.evidence.documentId}`;

  const steps = [
    trace.claim,
    ...trace.derivation,
    `${trace.evidence.documentName}${trace.evidence.page ? `, page ${trace.evidence.page}` : ""}`,
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Close trace"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-ink/20"
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Source of: ${trace.claim}`}
        className="relative flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-line bg-surface shadow-xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div>
            <div className="eyebrow">Where this comes from</div>
            <p className="mt-1 text-sm font-semibold text-ink">{trace.claim}</p>
          </div>
          <Button variant="ghost" onClick={onClose} aria-label="Close">
            ✕
          </Button>
        </header>

        <div className="border-b border-line px-5 py-4">
          <div className="eyebrow mb-3">Derivation</div>
          <ol className="space-y-0">
            {steps.map((step, index) => (
              <li key={`${step}-${index}`} className="relative pb-3 pl-6 last:pb-0">
                {index < steps.length - 1 ? (
                  <span aria-hidden className="absolute top-4 bottom-0 left-[0.3125rem] w-px bg-line-strong" />
                ) : null}
                <span
                  aria-hidden
                  className={cn(
                    "absolute top-1.5 left-0 size-[0.625rem] rounded-full border-2",
                    index === steps.length - 1
                      ? "border-accent bg-accent"
                      : "border-line-strong bg-surface",
                  )}
                />
                <span
                  className={cn(
                    "numeric text-xs",
                    index === steps.length - 1 ? "font-medium text-ink" : "text-ink-muted",
                  )}
                >
                  {step}
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div className="border-b border-line px-5 py-4">
          <div className="eyebrow mb-2">Source</div>
          <p className="text-sm font-medium text-ink">{trace.evidence.documentName}</p>
          <dl className="mt-2 space-y-1 text-xs text-ink-muted">
            {trace.evidence.section ? (
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 text-ink-faint">Section</dt>
                <dd>{trace.evidence.section}</dd>
              </div>
            ) : null}
            {trace.evidence.page ? (
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 text-ink-faint">Page</dt>
                <dd className="numeric">{trace.evidence.page}</dd>
              </div>
            ) : null}
            {source ? (
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 text-ink-faint">Provider</dt>
                <dd>{source.provider}</dd>
              </div>
            ) : null}
          </dl>
        </div>

        {value ? (
          <div className="border-b border-line px-5 py-4">
            <div className="eyebrow mb-2">Extracted value</div>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-xs text-ink-muted">{value.label}</span>
              <span className="numeric text-base font-semibold">{value.value}</span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Badge tone={STATUS_TONE[value.status]}>{value.status}</Badge>
              <Badge tone={confidenceBand(value.confidence) === "low" ? "caution" : "neutral"}>
                {Math.round(value.confidence * 100)}% confidence
              </Badge>
            </div>
            {value.note ? <p className="mt-2 text-xs text-ink-muted">{value.note}</p> : null}
          </div>
        ) : null}

        <div className="mt-auto border-t border-line px-5 py-4">
          <Link
            to={evidenceHref}
            onClick={onClose}
            className="inline-flex w-full items-center justify-center rounded-md border border-line-strong px-3 py-1.5 text-sm font-medium transition-colors hover:bg-surface-sunken"
          >
            Open in Evidence
          </Link>
        </div>
      </aside>
    </div>
  );
}
